import { CacheData, CacheOptions } from "../interfaces/all";

export class CacheManager {
  private static readonly DEFAULT_EXPIRATION_DAYS = 7;
  private static readonly DEFAULT_MAX_ITEMS = 1000;

  private static handleError(
    operation: string,
    key: string,
    error: unknown,
  ): void {
    console.error(`Erro ao ${operation} dados do cache (${key}):`, error);
    if (operation === "salvar") {
      this.remove(key);
    }
  }

  private static getCacheData<T>(key: string): CacheData<T> | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;
      return JSON.parse(cached) as CacheData<T>;
    } catch (error) {
      this.handleError("recuperar", key, error);
      return null;
    }
  }

  private static isExpired(timestamp: number, expirationDays: number): boolean {
    const expirationDate = new Date(timestamp);
    expirationDate.setDate(expirationDate.getDate() + expirationDays);
    return new Date() > expirationDate;
  }

  private static limitArrayItems<T>(data: T, maxItems: number): T {
    if (Array.isArray(data)) {
      return (data.length > maxItems ? data.slice(-maxItems) : data) as T;
    }
    return data;
  }

  static set<T>(
    key: string,
    data: T,
    options: CacheOptions = {},
    metadata?: Record<string, any>,
  ): void {
    try {
      const expirationDays =
        options.expirationDays || this.DEFAULT_EXPIRATION_DAYS;
      const maxItems = options.maxItems || this.DEFAULT_MAX_ITEMS;

      const cacheData: CacheData<T> = {
        data: this.limitArrayItems(data, maxItems),
        timestamp: Date.now(),
        metadata,
      };

      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      this.handleError("salvar", key, error);
    }
  }

  static get<T>(key: string, options: CacheOptions = {}): T | null {
    const expirationDays =
      options.expirationDays || this.DEFAULT_EXPIRATION_DAYS;
    const cacheData = this.getCacheData<T>(key);

    if (!cacheData) {
      return null;
    }

    if (this.isExpired(cacheData.timestamp, expirationDays)) {
      this.remove(key);
      return null;
    }

    return cacheData.data;
  }

  static remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      this.handleError("remover", key, error);
    }
  }

  static clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Erro ao limpar cache:", error);
    }
  }

  static getMetadata<T>(key: string): Record<string, any> | null {
    const cacheData = this.getCacheData<T>(key);
    return cacheData?.metadata || null;
  }

  static updateMetadata<T>(key: string, metadata: Record<string, any>): void {
    try {
      const cacheData = this.getCacheData<T>(key);
      if (!cacheData) return;

      cacheData.metadata = { ...cacheData.metadata, ...metadata };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      this.handleError("atualizar metadata", key, error);
    }
  }
}
